/**
 * HeroSection.tsx — hero de bienvenida del Home (PROMPT_MAESTRO §4.2 "Home").
 *
 * Extraído de `pages/Home.tsx` (2026-09-22) para sacar ~170 líneas de la page
 * y poder iterar el tratamiento de la capa ambiental sin tocar el resto.
 *
 * Capas, de atrás hacia adelante:
 *   1. Gradiente nocturno del `<section>` (fallback si no hay ilustración).
 *   2. Capa ambiental: ilustración de marca + scrim linear + viñeta radial.
 *   3. Estrellas decorativas.
 *   4. Copy + CTAs (`relative z-10`).
 */

import { Container } from '../../components/layout/Container';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';
import { Routes } from '../../lib/routes';
import { usePageCopy } from '../site-content/usePageCopy';
import type { SiteHero } from './api';
import { StarDot } from './ornaments';

/**
 * Ruta del asset ilustrativo del hero ("mujer luna") servido desde
 * `public/brand/`. Vive fuera del bundle de JS: Vite lo sirve directo bajo
 * `/brand/`. Si el archivo falta, el `onError` del `<img>` lo oculta y el
 * hero degrada al gradiente nocturno del `<section>` — sin romper layout,
 * pero también sin avisar: si el hero se ve liso, revisá que el WebP exista.
 */
const HERO_ILLUSTRATION_SRC = '/brand/hero-ceciluz-mujer-luna.webp';

export function HeroSection({ hero }: { hero: SiteHero | undefined }) {
  // Textos editables (admin /contenido) con fallback al jsonb actual.
  const title = usePageCopy(
    'home',
    'hero.title',
    hero?.title ?? 'Arte que sana. Energía que transforma. Alma que conecta.',
  );
  const subtitle = usePageCopy(
    'home',
    'hero.subtitle',
    hero?.subtitle ??
      'Terapias holísticas, arte consciente y acompañamiento espiritual para tu bienestar y despertar.',
  );
  const ctaPrimaryLabel = usePageCopy(
    'home',
    'hero.cta_primary.label',
    hero?.cta_primary?.label ?? 'Reservar terapia',
  );
  const ctaSecondaryLabel = usePageCopy(
    'home',
    'hero.cta_secondary.label',
    hero?.cta_secondary?.label ?? 'Explorar eventos',
  );
  const ctaPrimaryHref = hero?.cta_primary?.href ?? Routes.therapies;
  const ctaSecondaryHref = hero?.cta_secondary?.href ?? Routes.events;

  // Fuente única para la imagen ambiental del hero: si el admin subió una
  // imagen a `site_settings.hero.image_url`, esa gana; caso contrario, el
  // asset local del brand kit. El `onError` del `<img>` en la capa ambiental
  // se encarga de ocultar el elemento si el archivo no existe — la sección
  // degrada a hero violeta puro sin romper layout.
  const heroImageUrl = hero?.image_url ?? HERO_ILLUSTRATION_SRC;

  return (
    <section
      data-dark-bg="true"
      aria-label="Bienvenida"
      className={cn(
        'relative isolate overflow-hidden',
        // Gradiente "cielo nocturno" entre violetas profundos.
        'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
        'text-cream-100',
      )}
    >
      {/* ---- Capa 1: ambiental derecha — imagen mística como fondo del hero.
          La imagen deja de ser un rectángulo en la columna derecha del grid;
          ahora vive como una capa `absolute inset-0` con `object-cover` y
          `object-position: center right`, con máscara linear que desvanece
          hacia la izquierda y overlays violeta que la fusionan con el fondo
          del `<section>`. `pointer-events-none` + `aria-hidden` para que no
          interfiera con clicks ni SR. ---- */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* Imagen ambiental. `object-cover` llena la zona, `right` mantiene
            rostro/luna/loto en el foco cuando el hero es más ancho. Máscara
            linear izquierda→derecha para disolver el borde izquierdo hacia
            el texto. Va a `opacity-100`: la ilustración de marca ya es un
            violeta profundo que se funde solo con el fondo del `<section>`;
            atenuarla además la borraba. El scrim de abajo hace el trabajo. */}
        <img
          src={heroImageUrl}
          alt=""
          aria-hidden="true"
          // Encuadre: la ilustración es una composición simétrica con la
          // figura al centro. En mobile la capa ocupa todo el ancho, así que
          // `object-center` es lo único que la deja ver; con el encuadre
          // anterior (75 %) el recorte caía en el cielo vacío de la derecha y
          // el hero parecía liso. De `sm` en adelante la capa sólo ocupa la
          // mitad derecha y ahí sí conviene sesgar el encuadre.
          className="absolute inset-y-0 right-0 h-full w-full max-w-none object-cover object-center opacity-100 sm:w-[70%] sm:object-[75%_center] lg:w-[62%]"
          loading="eager"
          decoding="async"
          draggable={false}
          style={{
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 18%, black 40%, black 88%, transparent 100%)',
            maskImage:
              'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 18%, black 40%, black 88%, transparent 100%)',
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />

        {/* Overlay linear izquierda→derecha con el propio violeta del hero.
            Sólido sobre la columna de copy (protege legibilidad AA) y ya
            transparente al 62 % del ancho, donde vive el foco de la
            ilustración (rostro + corona de luna + loto).

            QA 2026-09-22: antes era `from-primary-900 via-primary-900/80
            to-transparent` sin posiciones. Dos problemas. Uno, los stops por
            defecto dejaban ~43 % de violeta encima del rostro. Dos, el `via`
            nunca existió: los colores del DS son `var(--color-*)` planas y
            Tailwind no puede aplicarles el modificador `/80`, así que esa
            parada se descartaba en silencio. Sumado al `opacity-80` del
            `<img>`, la ilustración quedaba casi invisible — por eso parecía
            que el hero no tenía imagen.

            Ahora: dos paradas, ambas en la escala que Tailwind sí genera
            (múltiplos de 5). Sólido hasta el 30 %, transparente al 65 %.

            Son dos scrims porque la dirección cambia con el breakpoint. En
            desktop el copy vive a la izquierda y la ilustración a la derecha →
            scrim horizontal. En mobile el copy va centrado sobre todo el
            ancho, así que un scrim horizontal lo dejaría ilegible sobre la
            figura → scrim vertical parejo, que baja la ilustración a fondo
            atmosférico y mantiene el contraste AA del texto.

            El de mobile es un velo plano con `opacity-*` sobre el elemento, no
            un `bg-primary-900/75`: el modificador de opacidad sobre color no
            funciona con los tokens del DS (son `var()` planas) y se
            descartaría en silencio, igual que el `via` de arriba. */}
        <div className="absolute inset-0 bg-primary-900 opacity-[0.78] sm:hidden" />
        <div className="absolute inset-0 hidden bg-gradient-to-r from-primary-900 from-30% to-transparent to-65% sm:block" />

        {/* Overlay radial adicional: atenúa el borde perimetral de la imagen
            que a veces se percibe como corte. Elipse centrada al 75% del
            ancho para no oscurecer el foco (rostro/luna/loto). Usamos
            `rgba(0,0,0,X)` — notación decimal, no viola la regla de tokens
            de color; sólo modula luminosidad. */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_50%,transparent_0%,transparent_55%,rgba(0,0,0,0.16)_85%,rgba(0,0,0,0.30)_100%)]" />
      </div>

      {/* Capa 2: decorativa de estrellas + halos. Existía antes; se mantiene
          intacta. Va DESPUÉS de la ambiental para que las estrellas queden
          por encima de la ilustración y refuerzen la sensación cósmica. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none">
        <div className="absolute -left-32 -top-32 h-72 w-72 rounded-pill bg-primary-500/15 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-pill bg-gold-500/10 blur-3xl" />
        <StarDot className="absolute left-[8%] top-[20%] text-xs opacity-80" />
        <StarDot className="absolute left-[24%] top-[68%] text-[10px] opacity-55" />
        <StarDot className="absolute left-[44%] top-[14%] text-sm opacity-50" />
        <StarDot className="absolute left-[36%] bottom-[18%] text-xs opacity-60" />
        <StarDot className="absolute left-[88%] top-[24%] text-[10px] opacity-70" />
        <StarDot className="absolute left-[78%] bottom-[28%] text-xs opacity-55" />
      </div>

      <Container>
        <div
          className={cn(
            // `relative z-10` — deja el copy + CTAs SOBRE las capas ambientales.
            'relative z-10 grid grid-cols-1 items-center gap-8 py-12 lg:gap-12 lg:py-20',
            'lg:grid-cols-[1.1fr_1fr]',
          )}
        >
          {/* ---- Columna izquierda: copy + CTAs ----
              `min-w-0` permite que la columna se contraiga al ancho real del
              grid en mobile. Sin esto, el `<h1>` con `text-[36px]` empuja la
              columna a su `min-content`, exceds el viewport y el texto se
              rompe mid-word ("Energ" + "ía") en lugar de wrappear por
              boundary normal. */}
          <div className="flex min-w-0 flex-col gap-6 text-center lg:text-left">
            <h1
              className={cn(
                'font-display text-cream-100',
                // Tamaño grande pero contenido — match referencia.
                'text-[36px] leading-[1.08] sm:text-[44px] lg:text-[54px]',
                // `break-words` extra-seguridad para títulos editables donde
                // el admin podría meter una palabra muy larga.
                'break-words',
              )}
            >
              {title}
            </h1>
            <p className="text-body text-cream-100/85 max-w-xl mx-auto lg:mx-0">{subtitle}</p>
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center lg:justify-start">
              <Button
                to={ctaPrimaryHref}
                variant="primary"
                size="lg"
                className="bg-gold-500 text-primary-900 hover:bg-gold-300"
              >
                {ctaPrimaryLabel}
              </Button>
              <Button
                to={ctaSecondaryHref}
                variant="outline"
                size="lg"
                className="border-cream-100 text-cream-100 hover:bg-cream-100 hover:text-primary-700"
              >
                {ctaSecondaryLabel}
              </Button>
            </div>
          </div>

          {/* Columna derecha del grid intencionalmente vacía: la ilustración
              ya vive como capa ambiental absoluta al inicio del `<section>`.
              Preservamos la 2ª columna del grid (`lg:grid-cols-[1.1fr_1fr]`)
              para que la primera no se expanda al 100% del ancho — así el
              copy respeta su medida legible y no compite con el rostro/luna
              del fondo ambiental derecho. */}
          <div aria-hidden="true" />
        </div>
      </Container>
    </section>
  );
}
